// src/store/sagas/feedSaga.ts
import {all, call, put, takeLatest, fork} from 'redux-saga/effects';
import type {SagaIterator} from 'redux-saga';
import type {FeedItem, FeedDetailItem} from '../../types/FeedItem';

import {
  // 목록
  fetchFeedsStart,
  fetchFeedsSuccess,
  fetchFeedsFailure,

  // 상세
  fetchFeedDetailStart,
  fetchFeedDetailSuccess,
  fetchFeedDetailFailure,

  // 상세 팝업 액션들
  saveEventRequest,
  removeSavedEventRequest,
  runBacktestRequest,
  addWatchlistRequest,
  removeWatchlistRequest,

  // 상태 에러 처리
  setFeedError,

  // 순매수 시계열 트리거 & 결과 액션
  fetchNetSeriesStart,
  fetchNetSeriesSuccess,
  fetchNetSeriesFailure,
} from '../slice/feedSlice';

import {
  fetchFeeds,
  fetchFeedDetail,
  saveEvent,
  removeSavedEvent,
  runBacktest,
  addWatchlist,
  removeWatchlist,
  fetchDailyNetSeries,
} from '../../api/feedApi';

/* ============================== Workers ================================ */

function* fetchFeedsSaga(
    action: ReturnType<typeof fetchFeedsStart>,
): SagaIterator {
  try {
    const {sector, sort} = action.payload;

    // fetchFeeds가 호출하는 백엔드 목록 API에서 mapping_audit를 내려줘야 한다.
    // 프론트는 여기서 점수를 재계산하지 않고, 응답에 포함된 audit를 보존한다.
    const response: FeedItem[] = (yield call(fetchFeeds, sector, sort)) as FeedItem[];

    yield put(fetchFeedsSuccess(response));
  } catch (e: any) {
    console.error('❌ fetchFeedsSaga error:', e);
    yield put(fetchFeedsFailure(e?.message ?? '목록 조회 실패'));
  }
}

function* fetchFeedDetailSaga(
    action: ReturnType<typeof fetchFeedDetailStart>,
): SagaIterator {
  try {
    const {feedId, sector} = action.payload;

    // 상세 API가 있으면 여기서 상세를 새로 받아온다.
    // 이 응답에도 mapping_audit가 포함되어야 한다.
    const response: FeedDetailItem = (yield call(fetchFeedDetail, {
      feedId,
      sector
    })) as FeedDetailItem;

    yield put(fetchFeedDetailSuccess(response));
  } catch (e: any) {
    console.error('❌ fetchFeedDetailSaga error:', e);
    yield put(fetchFeedDetailFailure(e?.message ?? '상세 조회 실패'));
  }
}

function* saveEventSaga(
    action: ReturnType<typeof saveEventRequest>,
): SagaIterator {
  try {
    yield put(setFeedError(null));
    yield call(saveEvent, action.payload);
  } catch (e) {
    console.error('❌ saveEventSaga error:', e);
    yield put(setFeedError('저장에 실패했습니다.'));
  }
}

function* removeSavedEventSaga(
    action: ReturnType<typeof removeSavedEventRequest>,
): SagaIterator {
  try {
    yield put(setFeedError(null));
    yield call(removeSavedEvent, action.payload.eventId);
  } catch (e) {
    console.error('❌ removeSavedEventSaga error:', e);
    yield put(setFeedError('저장 해제에 실패했습니다.'));
  }
}

function* runBacktestSaga(
    action: ReturnType<typeof runBacktestRequest>,
): SagaIterator {
  try {
    yield put(setFeedError(null));
    yield call(runBacktest, action.payload);
  } catch (e) {
    console.error('❌ runBacktestSaga error:', e);
    yield put(setFeedError('백테스트 실행에 실패했습니다.'));
  }
}

function* addWatchlistSaga(
    action: ReturnType<typeof addWatchlistRequest>,
): SagaIterator {
  try {
    yield put(setFeedError(null));
    yield call(addWatchlist, action.payload);
  } catch (e) {
    console.error('❌ addWatchlistSaga error:', e);
    yield put(setFeedError('관심종목 추가에 실패했습니다.'));
  }
}

function* removeWatchlistSaga(
    action: ReturnType<typeof removeWatchlistRequest>,
): SagaIterator {
  try {
    yield put(setFeedError(null));
    yield call(removeWatchlist, action.payload);
  } catch (e) {
    console.error('❌ removeWatchlistSaga error:', e);
    yield put(setFeedError('관심종목 제거에 실패했습니다.'));
  }
}

function* fetchNetSeriesSaga(
    action: ReturnType<typeof fetchNetSeriesStart>,
): SagaIterator {
  try {
    const {ticker, from, to} = action.payload;
    const data = (yield call(fetchDailyNetSeries, {ticker, from, to})) as any[];
    yield put(fetchNetSeriesSuccess({ticker, from, to, data}));
  } catch (e: any) {
    console.error('❌ fetchNetSeriesSaga error:', e);
    const {ticker, from, to} = action.payload;
    yield put(fetchNetSeriesFailure({ticker, from, to, error: e?.message ?? '데이터 조회 실패'}));
  }
}

/* ============================== Watchers =============================== */

export function* watchFeedSaga(): SagaIterator {
  yield all([
    takeLatest(fetchFeedsStart.type, fetchFeedsSaga),
    takeLatest(fetchFeedDetailStart.type, fetchFeedDetailSaga),

    takeLatest(saveEventRequest.type, saveEventSaga),
    takeLatest(removeSavedEventRequest.type, removeSavedEventSaga),
    takeLatest(runBacktestRequest.type, runBacktestSaga),
    takeLatest(addWatchlistRequest.type, addWatchlistSaga),
    takeLatest(removeWatchlistRequest.type, removeWatchlistSaga),
    takeLatest(fetchNetSeriesStart.type, fetchNetSeriesSaga),
  ]);
}

/* ============================== Root ==================================== */

export default function* feedRootSaga(): SagaIterator {
  yield all([fork(watchFeedSaga)]);
}
